import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, FolderOpen, Upload, File, FileText, Image, Trash2, Download, Plus, ChevronDown, ChevronRight, Phone, Mail, Globe, DollarSign, Calendar, Edit2, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const Vendors = ({ user }) => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  
  // New vendor form state
  const [newVendor, setNewVendor] = useState({
    name: '',
    category: 'venue',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    contract_amount: '',
    deposit_paid: '',
    notes: ''
  });

  const categories = [
    { value: 'venue', label: 'Venue' },
    { value: 'catering', label: 'Catering' },
    { value: 'photography', label: 'Photography' },
    { value: 'videography', label: 'Videography' },
    { value: 'florist', label: 'Florist' },
    { value: 'music', label: 'Music/DJ' },
    { value: 'cake', label: 'Cake/Bakery' },
    { value: 'attire', label: 'Attire' },
    { value: 'beauty', label: 'Hair & Makeup' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'officiant', label: 'Officiant' },
    { value: 'rentals', label: 'Rentals' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    loadVendors();
  }, [user.id]);

  const loadVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendor-vault/${user.id}`);
      setVendors(response.data);
    } catch (error) {
      console.error('Error loading vendors:', error);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVendor = async (e) => {
    e.preventDefault();
    if (!newVendor.name) {
      toast.error('Please enter a vendor name');
      return;
    }

    try {
      const response = await axios.post(`${API}/vendor-vault`, {
        user_id: user.id,
        ...newVendor,
        contract_amount: parseFloat(newVendor.contract_amount) || 0,
        deposit_paid: parseFloat(newVendor.deposit_paid) || 0,
        files: []
      });
      
      setVendors([...vendors, response.data]);
      setNewVendor({
        name: '',
        category: 'venue',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        website: '',
        contract_amount: '',
        deposit_paid: '',
        notes: ''
      });
      setShowAddVendor(false);
      toast.success('Vendor added successfully!');
    } catch (error) {
      toast.error('Failed to add vendor');
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    
    try {
      await axios.delete(`${API}/vendor-vault/${vendorId}`);
      setVendors(vendors.filter(v => v.id !== vendorId));
      toast.success('Vendor deleted');
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const handleUpdateVendor = async (vendorId, updates) => {
    try {
      await axios.put(`${API}/vendor-vault/${vendorId}`, updates);
      setVendors(vendors.map(v => v.id === vendorId ? { ...v, ...updates } : v));
      setEditingVendor(null);
      toast.success('Vendor updated');
    } catch (error) {
      toast.error('Failed to update vendor');
    }
  };

  const getCategoryLabel = (value) => {
    return categories.find(c => c.value === value)?.label || value;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      venue: '🏛️',
      catering: '🍽️',
      photography: '📸',
      videography: '🎥',
      florist: '💐',
      music: '🎵',
      cake: '🎂',
      attire: '👗',
      beauty: '💄',
      transportation: '🚗',
      officiant: '💒',
      rentals: '🪑',
      other: '📁'
    };
    return icons[category] || '📁';
  };

  // Group vendors by category
  const groupedVendors = vendors.reduce((acc, vendor) => {
    const cat = vendor.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(vendor);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-xl border-b border-stone-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Button
            data-testid="back-to-dashboard-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#57534E] hover:text-[#881337]"
          >
            <ArrowLeft className="mr-2" size={18} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Page Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FolderOpen className="text-[#881337]" size={32} />
              <h1 className="text-4xl font-serif font-light tracking-tight text-[#1C1917]">
                Vendor Vault
              </h1>
            </div>
            <p className="text-[#57534E]">Store all your vendor contacts, contracts, and important details in one place</p>
          </div>
          <Button
            onClick={() => setShowAddVendor(true)}
            className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
          >
            <Plus size={18} className="mr-2" />
            Add Vendor
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-4 border border-white/50">
            <p className="text-2xl font-serif text-[#881337]">{vendors.length}</p>
            <p className="text-sm text-[#57534E]">Total Vendors</p>
          </div>
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-4 border border-white/50">
            <p className="text-2xl font-serif text-[#1C1917]">
              ${vendors.reduce((sum, v) => sum + (v.contract_amount || 0), 0).toLocaleString()}
            </p>
            <p className="text-sm text-[#57534E]">Total Contracts</p>
          </div>
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-4 border border-white/50">
            <p className="text-2xl font-serif text-[#84A98C]">
              ${vendors.reduce((sum, v) => sum + (v.deposit_paid || 0), 0).toLocaleString()}
            </p>
            <p className="text-sm text-[#57534E]">Deposits Paid</p>
          </div>
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-4 border border-white/50">
            <p className="text-2xl font-serif text-amber-600">
              ${(vendors.reduce((sum, v) => sum + (v.contract_amount || 0), 0) - vendors.reduce((sum, v) => sum + (v.deposit_paid || 0), 0)).toLocaleString()}
            </p>
            <p className="text-sm text-[#57534E]">Remaining</p>
          </div>
        </div>

        {/* Add Vendor Form */}
        {showAddVendor && (
          <div className="bg-white rounded-2xl p-6 border border-stone-200 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-serif text-[#1C1917]">Add New Vendor</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddVendor(false)}>
                <X size={18} />
              </Button>
            </div>
            
            <form onSubmit={handleAddVendor} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Vendor Name *</label>
                  <Input
                    value={newVendor.name}
                    onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                    placeholder="e.g., Elegant Venues"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Category</label>
                  <select
                    value={newVendor.category}
                    onChange={(e) => setNewVendor({ ...newVendor, category: e.target.value })}
                    className="w-full h-10 rounded-xl border border-stone-200 px-3 text-sm"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Contact Name</label>
                  <Input
                    value={newVendor.contact_name}
                    onChange={(e) => setNewVendor({ ...newVendor, contact_name: e.target.value })}
                    placeholder="Contact person"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={newVendor.contact_email}
                    onChange={(e) => setNewVendor({ ...newVendor, contact_email: e.target.value })}
                    placeholder="email@vendor.com"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Phone</label>
                  <Input
                    value={newVendor.contact_phone}
                    onChange={(e) => setNewVendor({ ...newVendor, contact_phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Website</label>
                  <Input
                    value={newVendor.website}
                    onChange={(e) => setNewVendor({ ...newVendor, website: e.target.value })}
                    placeholder="www.vendor.com"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Contract Amount ($)</label>
                  <Input
                    type="number"
                    value={newVendor.contract_amount}
                    onChange={(e) => setNewVendor({ ...newVendor, contract_amount: e.target.value })}
                    placeholder="5000"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Deposit Paid ($)</label>
                  <Input
                    type="number"
                    value={newVendor.deposit_paid}
                    onChange={(e) => setNewVendor({ ...newVendor, deposit_paid: e.target.value })}
                    placeholder="1000"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#1C1917] mb-1 block">Notes</label>
                <Textarea
                  value={newVendor.notes}
                  onChange={(e) => setNewVendor({ ...newVendor, notes: e.target.value })}
                  placeholder="Contract details, meeting notes, important dates..."
                  className="rounded-xl min-h-24"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowAddVendor(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white">
                  Add Vendor
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Vendor List by Category */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#881337] border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-[#57534E]">Loading vendors...</p>
          </div>
        ) : vendors.length === 0 ? (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-12 border border-white/50 text-center">
            <FolderOpen className="mx-auto text-stone-300 mb-4" size={64} />
            <h3 className="text-xl font-serif text-[#1C1917] mb-2">Your Vendor Vault is Empty</h3>
            <p className="text-[#57534E] mb-6">Start adding your wedding vendors to keep all their details organized in one place.</p>
            <Button
              onClick={() => setShowAddVendor(true)}
              className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
            >
              <Plus size={18} className="mr-2" />
              Add Your First Vendor
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedVendors).map(([category, categoryVendors]) => (
              <div key={category} className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                {/* Category Header */}
                <div className="bg-stone-50 px-6 py-4 border-b border-stone-100">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getCategoryIcon(category)}</span>
                    <h3 className="font-serif text-lg text-[#1C1917]">{getCategoryLabel(category)}</h3>
                    <span className="text-sm text-[#57534E] bg-white px-2 py-0.5 rounded-full">{categoryVendors.length}</span>
                  </div>
                </div>

                {/* Vendors in Category */}
                <div className="divide-y divide-stone-100">
                  {categoryVendors.map(vendor => (
                    <div key={vendor.id} className="p-6">
                      {/* Vendor Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => setExpandedVendor(expandedVendor === vendor.id ? null : vendor.id)}
                        >
                          <div className="flex items-center gap-2">
                            <h4 className="text-lg font-medium text-[#1C1917]">{vendor.name}</h4>
                            {expandedVendor === vendor.id ? (
                              <ChevronDown size={18} className="text-stone-400" />
                            ) : (
                              <ChevronRight size={18} className="text-stone-400" />
                            )}
                          </div>
                          {vendor.contact_name && (
                            <p className="text-sm text-[#57534E]">Contact: {vendor.contact_name}</p>
                          )}
                        </div>
                        <div className="text-right">
                          {vendor.contract_amount > 0 && (
                            <p className="font-serif text-lg text-[#1C1917]">${vendor.contract_amount.toLocaleString()}</p>
                          )}
                          {vendor.deposit_paid > 0 && (
                            <p className="text-xs text-[#84A98C]">Deposit: ${vendor.deposit_paid.toLocaleString()}</p>
                          )}
                        </div>
                      </div>

                      {/* Contact Info - Always visible */}
                      <div className="flex flex-wrap gap-4 text-sm">
                        {vendor.contact_phone && (
                          <a href={`tel:${vendor.contact_phone}`} className="flex items-center gap-1 text-[#57534E] hover:text-[#881337]">
                            <Phone size={14} /> {vendor.contact_phone}
                          </a>
                        )}
                        {vendor.contact_email && (
                          <a href={`mailto:${vendor.contact_email}`} className="flex items-center gap-1 text-[#57534E] hover:text-[#881337]">
                            <Mail size={14} /> {vendor.contact_email}
                          </a>
                        )}
                        {vendor.website && (
                          <a href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#57534E] hover:text-[#881337]">
                            <Globe size={14} /> Website
                          </a>
                        )}
                      </div>

                      {/* Expanded Details */}
                      {expandedVendor === vendor.id && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                          {vendor.notes && (
                            <div className="mb-4">
                              <p className="text-xs text-[#A8A29E] uppercase tracking-wider mb-1">Notes</p>
                              <p className="text-sm text-[#57534E] whitespace-pre-wrap">{vendor.notes}</p>
                            </div>
                          )}
                          
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingVendor(vendor)}
                              className="rounded-full text-xs"
                            >
                              <Edit2 size={14} className="mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteVendor(vendor.id)}
                              className="rounded-full text-xs text-red-500 hover:text-red-600 hover:border-red-200"
                            >
                              <Trash2 size={14} className="mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Vendor Modal */}
      {editingVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-serif text-[#1C1917]">Edit Vendor</h2>
              <Button variant="ghost" size="sm" onClick={() => setEditingVendor(null)}>
                <X size={18} />
              </Button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateVendor(editingVendor.id, editingVendor);
            }} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Vendor Name</label>
                  <Input
                    value={editingVendor.name}
                    onChange={(e) => setEditingVendor({ ...editingVendor, name: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Category</label>
                  <select
                    value={editingVendor.category}
                    onChange={(e) => setEditingVendor({ ...editingVendor, category: e.target.value })}
                    className="w-full h-10 rounded-xl border border-stone-200 px-3 text-sm"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Contact Name</label>
                  <Input
                    value={editingVendor.contact_name || ''}
                    onChange={(e) => setEditingVendor({ ...editingVendor, contact_name: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Email</label>
                  <Input
                    value={editingVendor.contact_email || ''}
                    onChange={(e) => setEditingVendor({ ...editingVendor, contact_email: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Phone</label>
                  <Input
                    value={editingVendor.contact_phone || ''}
                    onChange={(e) => setEditingVendor({ ...editingVendor, contact_phone: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Website</label>
                  <Input
                    value={editingVendor.website || ''}
                    onChange={(e) => setEditingVendor({ ...editingVendor, website: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Contract Amount ($)</label>
                  <Input
                    type="number"
                    value={editingVendor.contract_amount || ''}
                    onChange={(e) => setEditingVendor({ ...editingVendor, contract_amount: parseFloat(e.target.value) || 0 })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1C1917] mb-1 block">Deposit Paid ($)</label>
                  <Input
                    type="number"
                    value={editingVendor.deposit_paid || ''}
                    onChange={(e) => setEditingVendor({ ...editingVendor, deposit_paid: parseFloat(e.target.value) || 0 })}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#1C1917] mb-1 block">Notes</label>
                <Textarea
                  value={editingVendor.notes || ''}
                  onChange={(e) => setEditingVendor({ ...editingVendor, notes: e.target.value })}
                  className="rounded-xl min-h-24"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setEditingVendor(null)} className="rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white">
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendors;
